// Mood check-in card.
//
// Two states share the same shell:
//   - Picking: 5-mood ladder + (after selection) factor chips, optional note,
//     and a Save button. Tapping a mood reveals the detail pane in-place;
//     the patient can save with or without filling out the details.
//   - Logged: today's mood + note + factors, with a sparkline of the last 7
//     days and an Edit affordance to overwrite (server upserts per IST day).
//
// Picker config (options + factors) is server-driven and lives on the same
// /me/today aggregate response that gates whether the card renders, so we
// don't fetch the config separately. See moodService.js for the contract.

import React, { useEffect, useMemo, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'
import WidgetCard from './WidgetCard'

// --- Visual constants --------------------------------------------------------

// Soft tone backgrounds — keyed off the server-provided `tone` hint on each
// MoodOption. Falls back to the neutral palette so unknown tones still render.
const TONE_BG = {
  sage:    { soft: '#E1ECE7', strong: '#6B9080', ink: '#3F5C50' },
  ocean:   { soft: '#E8EEF6', strong: '#1F3A5F', ink: '#1F3A5F' },
  neutral: { soft: '#F4F0E9', strong: '#9C8F77', ink: '#5C5444' },
  amber:   { soft: '#FBEFD9', strong: '#B97A2A', ink: '#7C5314' },
  rose:    { soft: '#FBEAE9', strong: '#C24D49', ink: '#8B302D' },
}

const toneOf = (key) => TONE_BG[key] || TONE_BG.neutral

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`

// --- Layout primitives -------------------------------------------------------

const Prompt = styled.p`
  margin: 0 0 16px;
  font-size: 17px;
  color: ${(p) => p.theme?.color?.ink || '#1F2937'};
  line-height: 1.4;
`

const TileRow = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 8px;
  @media (max-width: 420px) {
    gap: 6px;
  }
`

const Tile = styled.button`
  appearance: none;
  border: 2px solid
    ${(p) => (p.$selected ? p.$tone.strong : 'transparent')};
  background: ${(p) => p.$tone.soft};
  border-radius: 16px;
  padding: 14px 6px 12px;
  cursor: pointer;
  font-family: inherit;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  transition: transform 160ms ease-out, border-color 160ms ease-out, box-shadow 160ms ease-out;
  outline: none;

  &:hover {
    transform: translateY(-1px);
    border-color: ${(p) => p.$tone.strong};
  }

  &:focus-visible {
    box-shadow: 0 0 0 3px ${(p) => p.$tone.strong}33;
  }

  ${(p) =>
    p.$selected &&
    css`
      box-shadow: 0 4px 14px ${p.$tone.strong}22;
    `}
`

const TileEmoji = styled.span`
  font-size: 30px;
  line-height: 1;
`

const TileLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${(p) => p.$tone.ink};
`

const HelperText = styled.p`
  margin: 14px 0 0;
  font-size: 13px;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
  font-style: italic;
  animation: ${fadeIn} 200ms ease-out;
`

const Detail = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  animation: ${fadeIn} 200ms ease-out;
`

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
  margin-bottom: 8px;
`

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

const Chip = styled.button`
  appearance: none;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  border-radius: 999px;
  padding: 6px 12px;
  cursor: pointer;
  border: 1px solid
    ${(p) =>
      p.$on
        ? p.theme?.color?.primary || '#1F3A5F'
        : p.theme?.color?.borderStrong || '#D5CCBC'};
  background: ${(p) =>
    p.$on
      ? p.theme?.color?.primarySoft || '#E8EEF6'
      : p.theme?.color?.bgRaised || '#FFFFFF'};
  color: ${(p) =>
    p.$on
      ? p.theme?.color?.primary || '#1F3A5F'
      : p.theme?.color?.inkSoft || '#4B5563'};
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: background 140ms ease-out, border-color 140ms ease-out;

  &:hover {
    border-color: ${(p) => p.theme?.color?.primary || '#1F3A5F'};
  }
`

const NoteWrap = styled.div`
  margin-top: 14px;
`

const NoteToggle = styled.button`
  appearance: none;
  background: transparent;
  border: none;
  padding: 0;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  color: ${(p) => p.theme?.color?.primary || '#1F3A5F'};
  cursor: pointer;
  &:hover { text-decoration: underline; }
`

const NoteArea = styled.textarea`
  width: 100%;
  margin-top: 8px;
  border: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  border-radius: 12px;
  padding: 10px 12px;
  font-family: inherit;
  font-size: 14px;
  color: ${(p) => p.theme?.color?.ink || '#1F2937'};
  resize: vertical;
  min-height: 64px;
  background: ${(p) => p.theme?.color?.bgRaised || '#FFFFFF'};
  outline: none;
  transition: border-color 140ms ease-out, box-shadow 140ms ease-out;
  &:focus {
    border-color: ${(p) => p.theme?.color?.primary || '#1F3A5F'};
    box-shadow: 0 0 0 3px ${(p) => p.theme?.color?.primarySoft || '#E8EEF6'};
  }
`

const NoteCounter = styled.div`
  margin-top: 4px;
  font-size: 11px;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
  text-align: right;
`

const ActionRow = styled.div`
  margin-top: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`

const PrimaryAction = styled.button`
  appearance: none;
  border: none;
  background: ${(p) => p.theme?.color?.primary || '#1F3A5F'};
  color: white;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 18px;
  border-radius: 999px;
  cursor: pointer;
  transition: background 140ms ease-out, transform 140ms ease-out;
  &:hover {
    background: ${(p) => p.theme?.color?.primaryHover || '#15294A'};
  }
  &:active { transform: translateY(1px); }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const QuietLink = styled.button`
  appearance: none;
  background: transparent;
  border: none;
  padding: 0;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
  cursor: pointer;
  text-decoration: underline;
  &:hover { color: ${(p) => p.theme?.color?.ink || '#1F2937'}; }
`

// --- Logged state ------------------------------------------------------------

const LoggedHero = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  border-radius: 18px;
  background: ${(p) => p.$tone.soft};
`

const LoggedEmoji = styled.span`
  font-size: 44px;
  line-height: 1;
`

const LoggedTextWrap = styled.div`
  flex: 1;
  min-width: 0;
`

const LoggedLabel = styled.div`
  font-size: 17px;
  font-weight: 700;
  color: ${(p) => p.$tone.ink};
`

const LoggedTime = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
  margin-top: 2px;
`

const LoggedNote = styled.p`
  margin: 12px 0 0;
  font-size: 14px;
  color: ${(p) => p.theme?.color?.inkSoft || '#4B5563'};
  line-height: 1.5;
  white-space: pre-wrap;
`

const LoggedChipRow = styled.div`
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

const LoggedChip = styled.span`
  font-size: 12px;
  font-weight: 500;
  border-radius: 999px;
  padding: 4px 10px;
  background: ${(p) => p.theme?.color?.bgSubtle || '#F4F0E9'};
  color: ${(p) => p.theme?.color?.inkSoft || '#4B5563'};
  border: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
`

// --- Trend sparkline ---------------------------------------------------------

const TrendWrap = styled.div`
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
`

const TrendHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`

const TrendBars = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 6px;
  align-items: end;
  height: 56px;
`

const TrendBar = styled.div`
  background: ${(p) => p.$tone.strong};
  height: ${(p) => p.$h}%;
  min-height: ${(p) => (p.$empty ? '4px' : '8px')};
  width: 100%;
  border-radius: 6px 6px 2px 2px;
  opacity: ${(p) => (p.$empty ? 0.18 : p.$today ? 1 : 0.7)};
  transition: opacity 140ms ease-out;
`

const TrendCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
`

const TrendDayLabel = styled.span`
  font-size: 10px;
  font-weight: 600;
  text-align: center;
  color: ${(p) =>
    p.$today
      ? p.theme?.color?.primary || '#1F3A5F'
      : p.theme?.color?.inkMuted || '#6B7280'};
  letter-spacing: 0.04em;
  text-transform: uppercase;
`

// --- Component ---------------------------------------------------------------

const NOTE_LIMIT = 500

const dayShort = (isoDate) => {
  // 'YYYY-MM-DD' → 'Mon' / 'Tue' / ... in user's locale.
  try {
    const [y, m, d] = isoDate.split('-').map((s) => parseInt(s, 10))
    const dt = new Date(y, m - 1, d)
    return dt.toLocaleDateString(undefined, { weekday: 'short' }).slice(0, 3)
  } catch (e) {
    return ''
  }
}

const isoToday = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const formatLoggedTime = (iso) => {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  } catch (e) {
    return ''
  }
}

export default function MoodCheckInCard({ data, onRecord }) {
  const [picked, setPicked] = useState(null) // moodCode the user is currently picking
  const [factors, setFactors] = useState(() => new Set())
  const [note, setNote] = useState('')
  const [showNote, setShowNote] = useState(false)
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)

  // Derive everything off the raw `data?.options` reference so useMemo deps
  // stay stable across renders that don't actually change the mood config.
  const options = useMemo(() => data?.options || [], [data?.options])
  const factorOptions = useMemo(() => data?.factors || [], [data?.factors])
  const recent = useMemo(() => data?.recent || [], [data?.recent])
  const todays = data?.todaysMood

  const optionByCode = useMemo(() => {
    const m = {}
    for (const o of options) m[o.code] = o
    return m
  }, [options])

  // When today's mood arrives from server, exit editing mode and reset draft.
  useEffect(() => {
    if (todays && !editing) {
      setPicked(null)
      setFactors(new Set())
      setNote('')
      setShowNote(false)
    }
  }, [todays, editing])

  if (!data) return null
  // No options → backend hasn't provided picker config; nothing to render.
  if (options.length === 0 && !todays) return null

  // -------- Logged state --------
  if (todays && !editing) {
    const opt = optionByCode[todays.moodCode] || {
      label: todays.moodCode,
      emoji: '🙂',
      tone: 'neutral',
    }
    const tone = toneOf(opt.tone)
    return (
      <WidgetCard title="Mood check-in" badge="Logged">
        <LoggedHero $tone={tone}>
          <LoggedEmoji>{opt.emoji}</LoggedEmoji>
          <LoggedTextWrap>
            <LoggedLabel $tone={tone}>{opt.label} · {todays.score}/5</LoggedLabel>
            <LoggedTime>Logged at {formatLoggedTime(todays.checkedInAt)}</LoggedTime>
          </LoggedTextWrap>
        </LoggedHero>

        {todays.note && <LoggedNote>{todays.note}</LoggedNote>}

        {todays.factors && todays.factors.length > 0 && (
          <LoggedChipRow>
            {todays.factors.map((code) => {
              const f = factorOptions.find((x) => x.code === code)
              if (!f) return null
              return (
                <LoggedChip key={code}>
                  <span style={{ marginRight: 4 }}>{f.emoji}</span>
                  {f.label}
                </LoggedChip>
              )
            })}
          </LoggedChipRow>
        )}

        <TrendBlock recent={recent} optionByCode={optionByCode} />

        <ActionRow>
          <span style={{ fontSize: 13, color: '#6B7280' }}>
            Your therapist sees this trend in your weekly summary.
          </span>
          <QuietLink type="button" onClick={() => {
            setPicked(todays.moodCode)
            setFactors(new Set(todays.factors || []))
            setNote(todays.note || '')
            setShowNote(!!todays.note)
            setEditing(true)
          }}>
            Update
          </QuietLink>
        </ActionRow>
      </WidgetCard>
    )
  }

  // -------- Picker state --------

  const selectedOpt = picked ? optionByCode[picked] : null
  const selectedTone = selectedOpt ? toneOf(selectedOpt.tone) : null

  const toggleFactor = (code) => {
    setFactors((prev) => {
      const next = new Set(prev)
      if (next.has(code)) next.delete(code)
      else next.add(code)
      return next
    })
  }

  const submit = async () => {
    if (!picked || saving) return
    setSaving(true)
    try {
      await onRecord({
        moodCode: picked,
        note: note.trim() || undefined,
        factors: factors.size ? Array.from(factors) : undefined,
      })
      // useToday refresh will swap us back to the logged-state branch.
    } finally {
      setSaving(false)
      setEditing(false)
    }
  }

  return (
    <WidgetCard title="Mood check-in" badge={editing ? 'Editing' : null}>
      <Prompt>{data.prompt || 'How are you feeling today?'}</Prompt>

      <TileRow role="radiogroup" aria-label="Pick a mood">
        {options.map((m) => {
          const tone = toneOf(m.tone)
          const isSelected = picked === m.code
          return (
            <Tile
              key={m.code}
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-label={m.label}
              $tone={tone}
              $selected={isSelected}
              onClick={() => setPicked(m.code)}
            >
              <TileEmoji>{m.emoji}</TileEmoji>
              <TileLabel $tone={tone}>{m.label}</TileLabel>
            </Tile>
          )
        })}
      </TileRow>

      {selectedOpt && (
        <HelperText>{selectedOpt.helperText}</HelperText>
      )}

      {selectedOpt && (
        <Detail>
          {factorOptions.length > 0 && (
            <>
              <SectionLabel>What&apos;s behind it? <span style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0, color: '#6B7280' }}>(optional)</span></SectionLabel>
              <ChipRow>
                {factorOptions.map((f) => (
                  <Chip
                    key={f.code}
                    type="button"
                    aria-pressed={factors.has(f.code)}
                    $on={factors.has(f.code)}
                    onClick={() => toggleFactor(f.code)}
                  >
                    <span>{f.emoji}</span>
                    <span>{f.label}</span>
                  </Chip>
                ))}
              </ChipRow>
            </>
          )}

          <NoteWrap>
            {!showNote ? (
              <NoteToggle type="button" onClick={() => setShowNote(true)}>
                + Add a private note
              </NoteToggle>
            ) : (
              <>
                <SectionLabel>A few words for yourself <span style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0, color: '#6B7280' }}>(optional)</span></SectionLabel>
                <NoteArea
                  rows={3}
                  maxLength={NOTE_LIMIT}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="What's contributing? Anything you'd want to bring up in your next session?"
                />
                <NoteCounter>{note.length}/{NOTE_LIMIT}</NoteCounter>
              </>
            )}
          </NoteWrap>

          <ActionRow>
            <QuietLink
              type="button"
              onClick={() => {
                setPicked(null)
                setFactors(new Set())
                setNote('')
                setShowNote(false)
                if (editing) setEditing(false)
              }}
            >
              {editing ? 'Cancel' : 'Reset'}
            </QuietLink>
            <PrimaryAction
              type="button"
              onClick={submit}
              disabled={saving}
              style={selectedTone ? { background: selectedTone.strong } : undefined}
            >
              {saving ? 'Saving…' : editing ? 'Update mood' : 'Save check-in'}
            </PrimaryAction>
          </ActionRow>
        </Detail>
      )}

      <TrendBlock recent={recent} optionByCode={optionByCode} />
    </WidgetCard>
  )
}

function TrendBlock({ recent, optionByCode }) {
  if (!recent || recent.length === 0) return null

  // Hide the trend block entirely if there's not a single logged day yet —
  // showing seven empty bars for a brand-new patient is just visual noise.
  const hasAny = recent.some((p) => p && p.score != null)
  if (!hasAny) return null

  const today = isoToday()
  return (
    <TrendWrap>
      <TrendHeader>
        <SectionLabel>Last 7 days</SectionLabel>
      </TrendHeader>
      <TrendBars>
        {recent.map((p) => {
          const opt = p.moodCode ? optionByCode[p.moodCode] : null
          const tone = toneOf(opt?.tone)
          const isToday = p.date === today
          const empty = p.score == null
          const h = empty ? 12 : Math.max(20, (p.score / 5) * 100)
          return (
            <TrendCol key={p.date}>
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: 40 }}>
                <TrendBar $tone={tone} $h={h} $empty={empty} $today={isToday} />
              </div>
              <TrendDayLabel $today={isToday}>{dayShort(p.date)}</TrendDayLabel>
            </TrendCol>
          )
        })}
      </TrendBars>
    </TrendWrap>
  )
}
